import { Channel } from "@kosmod/pancake";
import { SceneSessionChannel } from "./channel";
import { createJanitor, ExternalPromise, ID, Logger, MapAction, MapND, Predictor2, SimpleMapPredictor, SubjectData, updateMap, updateSimpleMap } from "@kosmod/kit";
import { Player, PlayerID } from "@kosmod/sesame";
import { Mini, MiniEvent, MiniEvents, MiniID, PhysicsDice, PhysicsDiceID, SceneID, Surface, SurfaceID, TerrainCanvas, TerrainCanvasID, TerrainChunk, TerrainChunkEvent, TheatrePosition, ToolStateRaw, updateMini } from "../../defs";
import { ChunkAction, ChunkKey } from "@kosmod/stew";
import { Subject } from "rxjs";
import { K3D } from "../../../graphics_3d/defs";
import { Events, RawSessionEvent } from "./events";
import { RawSessionAction, SceneSessionAction } from "./actions";

export type SceneSessionClient = ReturnType<typeof createSceneSessionClient>;

export const createSceneSessionClient = (
  port: Channel.Port.Client<SceneSessionChannel>,
  logger: Logger,
  self: Player
) => {
  const janitor = createJanitor();
  let sceneId: SceneID | null = null;
  let actionSequence = 0;

  const minis = new SubjectData(new Map<MiniID, SubjectData<Mini>>);
  // const floors = new SubjectData(new Map<SurfaceID, Surface>());
  const canvases = new SubjectData(new Map<TerrainCanvasID, TerrainCanvas>());
  const onChunkChange = new Subject<[ChunkKey, ChunkAction][]>();
  const dice = new SubjectData(new Map<PhysicsDiceID, PhysicsDice>())
  const surfaces = new SubjectData(new Map<SurfaceID, Surface>())

  const defaultSurfaceId = new SubjectData<SurfaceID | null>(null);

  const diceTransforms = new SubjectData(new Map<PhysicsDiceID, K3D.Transform>());

  const players = new SubjectData(new Map<PlayerID, {
    id: PlayerID,
    cursor: SubjectData<null | TheatrePosition>,
    tool: null | ToolStateRaw
  }>());

  const chunks = new MapND<[canvas: TerrainCanvasID, x: number, y: number], TerrainChunk>();

  const applyMinisUpdate = (event: MapAction<MiniID, Mini, MiniEvent>) => updateMap({
    add(key, value) {
      minis.current.set(key, new SubjectData(value))
      minis.notify();
    },
    remove(key) {
      minis.current.delete(key);
      minis.notify();
    },
    update(key, update) {
      const s = minis.current.get(key);
      s!.set(updateMini(s!.current, update))
    },
  }, event)
  const applyChunkUpdate = (e: MapAction<[TerrainCanvasID, number, number], TerrainChunk, TerrainChunkEvent>) => updateMap({
    add([canvasId, x, y], value) {
      chunks.set([canvasId, x, y], value);
    },
    remove([canvasId, x, y]) {
      chunks.delete([canvasId, x, y]);
    },
    update([canvasId, x, y], update) {
      chunks.update([canvasId, x, y], prev => (prev.data = update.data, prev));
    },
  }, e)

  const predictions = {
    minis: new Predictor2<MiniID, Mini, MiniEvent>({
      flattenKey: k => k,
      flattenSeq: s => s,
      apply: applyMinisUpdate,
      read: k => minis.current.get(k)?.current || null,
      write: (k, v) => minis.current.get(k)!.set(v),
      remove: k => (minis.current.delete(k), minis.notify())
    }, {
      logger: logger.createSubsystem('predictor:mini')
    }),
    chunks: new Predictor2<[TerrainCanvasID, x: number, y: number], TerrainChunk, TerrainChunkEvent, [number, TerrainCanvasID, x: number, y: number]>({
      flattenKey: k => k.join(':'),
      flattenSeq: s => s.join(':'),
      apply: applyChunkUpdate,

      read: k => chunks.get(k) || null,
      write: (k, v) => chunks.update(k, c => (c.data = v.data, c)),
      remove: k => chunks.delete(k),
    }, {
      stableIds: true,
      //logger: logger.createSubsystem('predictor:chunks')
    }),
    canvases: new SimpleMapPredictor(canvases.current),
    dice: new SimpleMapPredictor(dice.current)
  }

  const ready = new ExternalPromise();

  const createEventHandler = (mode: 'predict' | 'reconcile') => (event: RawSessionEvent, seq: number | null) => {
    switch (event.type) {
      case 'load-scene':
        surfaces.current.clear();
        canvases.current.clear();
        minis.current.clear();
        players.current.clear();
        dice.current.clear()
        chunks.clear();
        diceTransforms.current.clear();

        for (const mini of event.scene.minis)
          minis.current.set(mini.id, new SubjectData(mini));

        for (const surface of event.scene.surfaces)
          surfaces.current.set(surface.id, surface);

        for (const canvas of event.scene.canvases) {
          canvases.current.set(canvas.id, canvas);
        }
        for (const chunk of event.scene.chunks) {
          chunks.set([chunk.canvas, chunk.x, chunk.y], chunk);
        }
        for (const player of event.players) {
          players.current.set(player.id, { ...player, cursor: new SubjectData(player.cursor) });
        }
        for (const die of event.scene.dice) {
          dice.current.set(die.id, die);
        }
        defaultSurfaceId.set(event.scene.defaultSurface);

        minis.notify();
        canvases.notify();
        players.notify();
        surfaces.notify();
        dice.notify()

        onChunkChange.next([...chunks.map((key, _) => [key, 'add'] as [ChunkKey,  ChunkAction])])

        ready.resolve();
        logger.log('Scene Loaded', { surfaces, canvases, chunks, minis })
        return;

      case 'minis':
        if (seq !== null) {
          if (mode === 'reconcile') {
            predictions.minis.reconcile(event.a, seq);
          } else {
            predictions.minis.record(event.a, seq);
          }
        } else {
          applyMinisUpdate(event.a);
        }
        return;
      case 'dice': {
        // if (seq) {
        //   if (mode === 'predict') {
        //     predictions.dice.record(event.a, seq)
        //   } else {
        //     predictions.dice.reconcile(event.a, seq)
        //   }
        //   dice.notify();
        // } else {
        //   dice.update(prev => updateSimpleMap(prev, event.a));
        // }


        dice.update(prev => updateSimpleMap(prev, event.a));
        return;
      }
      
      case 'chunks':
        for (const chunkAction of event.chunks) {
          logger.log('action', chunkAction)
          const [id, x, y] = chunkAction.key;
          if (seq !== null) {
            if (mode === 'reconcile') {
              const successfulPrediction = predictions.chunks.reconcile(chunkAction, [seq, id, x, y]);
              if (successfulPrediction)
                return;
            } else {
              predictions.chunks.record(chunkAction, [seq, id, x, y]);
            }
          } else {
            logger.log('Emitting regular chunk update', chunkAction)
            applyChunkUpdate(chunkAction)
          }
        }

        onChunkChange.next(event.chunks.map((event) => [event.key, event.type]) )

        return;
      case 'canvases':
        if (seq) {
          if (mode === 'predict') {
            predictions.canvases.record(event.canvases, seq)
          } else {
            predictions.canvases.reconcile(event.canvases, seq)
          }
        } else {
          updateSimpleMap(canvases.current, event.canvases);
        }
        canvases.notify();
        return;
      case 'players':
        updateMap({
          add(key, value) {
            players.update(p => p.set(key, { ...value, cursor: new SubjectData(value.cursor) }))
          },
          remove(key) {
            players.update(p => void p.delete(key))
          },
          update(key, update) {
            // logger.log([...players.current.keys()], key, players.current.get(key))
            players.update(p => p.set(key, { ...p.get(key)!, tool: update.tool }))
            // logger.log([...players.current.keys()], key, players.current.get(key))
          },
        }, event.players);
        return;
      case 'sync':
        for (const playerSync of event.players) {
          const player = players.current.get(playerSync.id);
          if (player)
            player.cursor.set(playerSync.cursor);
        }
        if (event.dice) {
          let diceAdded = false;

          for (const [diceId, diceTransform] of event.dice) {
            let diceTransformStored = diceTransforms.current.get(diceId);
            if (!diceTransformStored) {
              diceTransformStored = {
                position: { ...diceTransform.position },
                rotation: { ...diceTransform.rotation }
              }
              diceTransforms.current.set(diceId, diceTransformStored)
              diceAdded = true;
            }
            
            diceTransformStored.position.x = diceTransform.position.x;
            diceTransformStored.position.y = diceTransform.position.y;
            diceTransformStored.position.z = diceTransform.position.z;

            diceTransformStored.rotation.x = diceTransform.rotation.x;
            diceTransformStored.rotation.y = diceTransform.rotation.y;
            diceTransformStored.rotation.z = diceTransform.rotation.z;
            diceTransformStored.rotation.w = diceTransform.rotation.w;
          }
          if (diceAdded)
            diceTransforms.notify();
        }
        return;
    }
  }


  const predictionHandler = createEventHandler('predict');
  const reconcileHandler = createEventHandler('reconcile');

  janitor.add(port.receive.subscribe(({ e: message, meta }) => {
    if (message.type !== 'sync')
      logger.createSubsystem('RECEIVE').log(message, meta?.ackSeq || '(no seq)');
    reconcileHandler(message, meta && meta.ackSeq)
  }));

  return {
    ready: ready.promise,
    send(action: RawSessionAction) {
      if (action.type !== 'sync')
        logger.createSubsystem('SEND').log(action);
      const wrapped: SceneSessionAction = {
        a: action,
        meta: { actionSeq: ++actionSequence }
      };
      port.send(wrapped);

      const prediction = guessEventFromAction(action, self.id);
      if (prediction)
        predictionHandler(prediction, actionSequence)
    },
    subjects: {
      onChunkChange,
      chunks,
      minis,
      canvases,
      players,
      dice,
      diceTransforms,
      surfaces,
      defaultSurfaceId,
    },
    disconnect() {
      janitor.cleanup();
    }
  }
}


function guessEventFromAction(action: RawSessionAction, self: PlayerID): null | RawSessionEvent {
  switch (action.type) {
    case 'move-mini':
      return Events.Session.Minis(
        Events.Minis.update(action.miniId, MiniEvents.Move(action.position, action.animation))
      );
    case 'set-mini-logic':
      return Events.Session.Minis(
        Events.Minis.update(action.miniId, MiniEvents.Logic(action.logicIndex, action.logicState))
      );
    case 'remove-mini':
      return Events.Session.Minis({
        type: 'remove',
        key: action.miniId,
      })
    case 'add-canvas':
      const canvasID: TerrainCanvasID = ID('terrain_canvas');
      return Events.Session.Canvases(
        Events.Canvases.add(canvasID, {
          id: canvasID,
          surface: action.surface,
          palette: action.palette || [],
        })
      )
    case 'place-mini':
      const key = ID("mini");
      return Events.Session.Minis(
        Events.Minis.add(key, {
          id: key,
          // model: action.model,
          position: action.position,
          animation: null,
          networkState: 'pending',
          logic: action.logic,
          rotation: 0,
        })
      );
    // case 'draw-terrain-chunk':
    //   return Events.Session.Chunks([
    //     ...action.add.map(chunk => Events.Chunks.add([chunk.canvas, chunk.x, chunk.y], chunk)),
    //     ...action.remove.map(key => Events.Chunks.remove(key)),
    //     ...action.update.map(chunk => Events.Chunks.update([chunk.canvas, chunk.x, chunk.y], { type: 'modify', data: chunk.data }))
    //   ]);
    case 'add-die': {
      const key: PhysicsDiceID = ID("theatre-physics-die");
      return null;
      // return {
      //   type: 'dice', a: { type: 'add', add: {
      //     id: key,
      //     dieTemplate: action.diceTemplateId,
      //     initialTransform: action.initialTransform,
      //     simulator: self,
      //     result: null
      //   }, key }
      // }
    }
    default:
      return null;
  }
}